import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Container from '@/components/ui/Container';
import FadeIn from '@/components/animations/FadeIn';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { ChevronRight, NotebookPen } from 'lucide-react';
import { motion } from 'framer-motion';

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface PageLayoutProps {
  title: string;
  icon?: ReactNode;
  description?: string;
  children: ReactNode;
  containerSize?: "default" | "small" | "medium" | "large" | "full";
  breadcrumbs?: BreadcrumbItem[];
  breadcrumbIcon?: ReactNode;
  breadcrumbRootPath?: string;
}

const PageLayout = ({
  title,
  icon,
  children,
  containerSize = "full",
  breadcrumbs,
  breadcrumbIcon = <NotebookPen size={14} />,
  breadcrumbRootPath = "/dashboard/workdesk"
}: PageLayoutProps) => {
  const { isMobile } = useIsMobile();
  
  return (
    <Container size={containerSize}>
      <div className="space-y-6">
        <div className={isMobile ? 'mt-6' : 'mt-2'}>
          {(title || icon) && (
            <div className={`
              mb-6 
              overflow-hidden 
              rounded-3xl
              bg-gradient-to-br 
              from-accent/10 
              via-purple-500/10 
              to-transparent 
              dark:from-accent/20
              dark:via-purple-500/20
              dark:to-transparent/10
              border
              border-accent/20
              dark:border-white/20
              shadow-lg
            `}>
              <div className="relative">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] 
                  from-purple-500/20 
                  via-accent/10 
                  to-transparent 
                  dark:from-purple-500/30
                  dark:via-accent/20
                  dark:to-transparent
                  opacity-40" />
                <div className="absolute inset-0 backdrop-blur-md bg-white/5 dark:bg-black/10" />
                
                {/* Content */}
                <div className="relative px-6 py-4 md:px-8 md:py-6 flex items-center justify-between gap-4">
                  <motion.div 
                    className="h-12 w-12 flex-shrink-0 rounded-xl bg-gradient-to-br from-accent to-purple-500/80 flex items-center justify-center text-white shadow-xl shadow-accent/20 border border-white/20"
                    initial={{ rotate: -10, scale: 0.9 }}
                    animate={{ rotate: -3, scale: 1 }}
                    whileHover={{ rotate: 0, scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  >
                    {icon}
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                    className="min-w-0 flex-shrink flex-grow"
                  >
                    <div className="flex flex-col max-w-[240px] sm:max-w-[300px] md:max-w-[400px] lg:max-w-[500px] w-full">
                      <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent drop-shadow-sm truncate pb-1 text-left w-full">
                        {title}
                      </h1>
                    </div>
                  </motion.div>
                    
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                    className="flex items-center gap-3 z-10 flex-shrink-0"
                  >
                    {/* Breadcrumbs */}
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap overflow-hidden max-w-[300px] md:max-w-[400px]">
                      <Link to={breadcrumbRootPath} className="flex items-center hover:text-primary">
                        {breadcrumbIcon}
                        <span className="sr-only">{breadcrumbs?.[0]?.label || "Home"}</span>
                      </Link>
                      
                      {breadcrumbs ? (
                        <>
                          {breadcrumbs.map((crumb) => (
                            <span key={crumb.label} className="flex items-center">
                              <ChevronRight size={14} />
                              {crumb.label}
                            </span>
                          ))}
                        </>
                      ) : (
                        <>
                          <ChevronRight size={14} />
                          <span className="text-foreground">{title.replace('Goal', 'Project')}</span>
                        </>
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          )}
          
          <FadeIn>
            {children}
          </FadeIn>
        </div>
      </div>
    </Container>
  );
};

export default PageLayout;
